import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import { Image } from "expo-image";
import React, { useState } from "react";
import { StyleSheet, Text, View, useWindowDimensions } from "react-native";
import Ionicons from "@react-native-vector-icons/ionicons";

const DESKTOP_HERO_WIDTH = 1136;
const DESKTOP_HERO_HEIGHT = 260;
const GRAIN_TILES = 16;
const DOT_COLUMNS = 14;
const DOT_ROWS = 7;

function getHeroImageHeight(width: number): number {
  if (width < 400) {
    return 150;
  }

  if (width < 540) {
    return 170;
  }

  if (width === 540) {
    return 250;
  }

  if (width >= 1024) {
    return 450;
  }

  return 350;
}

function DesktopHero() {
  const [width, setWidth] = useState(DESKTOP_HERO_WIDTH);
  const scale = width / DESKTOP_HERO_WIDTH;

  return (
    <View
      style={styles.desktop}
      onLayout={(event) => {
        const nextWidth = event.nativeEvent.layout.width;
        if (nextWidth > 0 && nextWidth !== width) {
          setWidth(nextWidth);
        }
      }}
    >
      {Array.from({ length: GRAIN_TILES }, (_, index) => (
        <Image
          key={index}
          source={require("@/assets/images/desktop-hero/grains.png")}
          pointerEvents="none"
          style={{
            position: "absolute",
            width: 180 * scale,
            height: 180 * scale,
            opacity: 0.25,
            left: (index % 8) * 180 * scale,
            top: Math.floor(index / 8) * 180 * scale,
          }}
          contentFit="cover"
        />
      ))}
      <Image
        source={require("@/assets/images/desktop-hero/arcs.svg")}
        style={StyleSheet.absoluteFill}
        contentFit="fill"
        pointerEvents="none"
      />
      <View
        pointerEvents="none"
        style={{
          position: "absolute",
          left: -113 * scale,
          top: 221 * scale,
          width: 191 * scale,
          height: 94 * scale,
        }}
      >
        {Array.from({ length: DOT_COLUMNS * DOT_ROWS }, (_, index) => {
          const column = index % DOT_COLUMNS;
          const row = Math.floor(index / DOT_COLUMNS);
          return (
            <View
              key={index}
              style={{
                position: "absolute",
                left: (column * 191) / (DOT_COLUMNS - 1) * scale,
                top: (row * 94) / (DOT_ROWS - 1) * scale,
                width: 4 * scale,
                height: 4 * scale,
                borderRadius: 4 * scale,
                backgroundColor: "rgba(255,255,255,0.9)",
              }}
            />
          );
        })}
      </View>
      <View
        style={{
          position: "absolute",
          left: 48 * scale,
          top: 48 * scale,
          width: 520 * scale,
        }}
      >
        <View
          style={[
            styles.badge,
            {
              paddingHorizontal: 14 * scale,
              paddingVertical: 7 * scale,
              gap: 6 * scale,
            },
          ]}
        >
          <Ionicons name="headset" size={Math.max(12, 13 * scale)} color="#059669" />
          <Text
            style={{
              color: "#059669",
              fontSize: 11 * scale,
              fontFamily: "PlusJakartaSansBold",
              letterSpacing: 0.4 * scale,
            }}
          >
            24/7 VEHICLE ASSISTANCE
          </Text>
        </View>
        <Text
          style={{
            color: "#fff",
            fontFamily: "PlusJakartaSansBold",
            fontSize: 38 * scale,
            lineHeight: 44 * scale,
            letterSpacing: -0.8 * scale,
            marginTop: 14 * scale,
          }}
        >
          Vehicle Help. Anytime. Anywhere.
        </Text>
        <Text
          style={{
            color: "#e2e8f0",
            fontFamily: "PlusJakartaSans",
            fontSize: 17 * scale,
            lineHeight: 26 * scale,
            marginTop: 6 * scale,
          }}
        >
          Fast. Safe. Reliable.
        </Text>
      </View>
      <Image
        source={require("@/assets/images/desktop-hero/shadow.svg")}
        style={{
          position: "absolute",
          left: 754 * scale,
          top: 198 * scale,
          width: 320 * scale,
          height: 30 * scale,
        }}
        contentFit="fill"
        pointerEvents="none"
      />
      <Image
        source={require("@/assets/images/desktop-hero/truck.png")}
        style={{
          position: "absolute",
          left: 711 * scale,
          top: 57 * scale,
          width: 373 * scale,
          height: 176 * scale,
        }}
        contentFit="contain"
        pointerEvents="none"
      />
      <Image
        source={require("@/assets/images/desktop-hero/shield.svg")}
        style={{
          position: "absolute",
          left: 897 * scale,
          top: 41 * scale,
          width: 77 * scale,
          height: 87 * scale,
        }}
        contentFit="contain"
        pointerEvents="none"
      />
    </View>
  );
}

export function HomeHeroCard() {
  const { width } = useWindowDimensions();
  const desktop = useDesktopLayout();

  if (desktop) {
    return <DesktopHero />;
  }

  return (
    <View style={styles.container}>
      <Image
        source={require("@/assets/images/hero-card.png")}
        style={[styles.heroImage, { height: getHeroImageHeight(width) }]}
        contentFit="contain"
        contentPosition="center"
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginBottom: 24,
  },
  heroImage: {
    width: "100%",
  },
  desktop: {
    width: "100%",
    aspectRatio: DESKTOP_HERO_WIDTH / DESKTOP_HERO_HEIGHT,
    marginBottom: 28,
    borderRadius: 12,
    overflow: "hidden",
    backgroundColor: "#0B908C",
  },
  badge: {
    alignSelf: "flex-start",
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#ECFDF5",
    borderRadius: 999,
  },
});
